/**
 * GameStage — host for a single ClientGame inside a PixiJS Application.
 * Surfaces init/load failures visibly. Visible 3-2-1 countdown overlay,
 * platform-aware in-game control hint pill.
 */
import { useEffect, useRef, useState } from 'react';
import type { ClientGame, ClientGameContext, RoundOutcome, ServerPlayerRef, StageViewport } from '@tt/types';
import { audio, type Ambience } from '@tt/audio';
import { createControlsLayer, detectPlatform, type ControlsLayerHandle } from '@tt/input';
import { loadGame, loadGameManifest } from './loaders';
import { StageBackdrop } from './StageBackdrop';
import { preloadMatchCharacterTextures, preloadMatch3DFaceCanvases, clearMatchCharacterTextures } from './iconTexture';

const GAME_AMBIENCE: Record<string, Ambience> = {
  'last-lantern': 'cellar',
  'cellar-sweep': 'cellar',
  'cipher-cellar': 'cellar',
  'ouija-roulette': 'cellar',
  'one-of-us': 'cellar',
  'phantom-pursuit': 'cellar',
  'witch-switch': 'cellar',
  'heist-heist': 'cellar',
  'rope-walk': 'forest',
  'trapline': 'forest',
  'splatter-square': 'forest',
  'stick-brawl': 'forest',
  'lumber-tumble': 'forest',
  'banner-bash': 'forest',
  'climb-spire': 'forest',
  'pirate-pulley': 'sea',
  'sky-sieve': 'storm',
};

interface InputBindings {
  primary: { label: string; key?: string[] };
  alt?: Array<{ label: string; key?: string[] }>;
}

interface GameMeta {
  name: string;
  pitch: string;
  category: string;
  chaoticness: number;
  durationSec: number;
  controls: string[];
  tutorial?: { headline: string; tip: string };
}

interface Props {
  matchId: string;
  gameId: string;
  startsAt: number;
  endsAt: number;
  params: unknown;
  mySlot: number;
  players: ServerPlayerRef[];
  tickPayload: unknown;
  result: RoundOutcome | null;
  sendInput(payload: unknown): void;
  inputBindings?: InputBindings | undefined;
  gameMeta?: GameMeta | undefined;
  eventMode?: 'standard' | 'lightning' | 'comeback' | 'chaos' | 'cozy';
}

export function GameStage(props: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const tickHandlersRef = useRef<Set<(p: unknown) => void>>(new Set());
  const endHandlersRef = useRef<Set<(r: RoundOutcome) => void>>(new Set());
  const startHandlersRef = useRef<Set<(p: unknown) => void>>(new Set());
  const lastTickRef = useRef<unknown>(undefined);
  const lastResultRef = useRef<RoundOutcome | null>(null);
  const [bootError, setBootError] = useState<string | null>(null);
  const [phase, setPhase] = useState<'booting' | 'countdown' | 'playing' | 'ended'>('booting');

  useEffect(() => {
    if (props.tickPayload !== undefined && props.tickPayload !== lastTickRef.current) {
      lastTickRef.current = props.tickPayload;
      for (const cb of tickHandlersRef.current) cb(props.tickPayload);
    }
  }, [props.tickPayload]);

  useEffect(() => {
    if (props.result && props.result !== lastResultRef.current) {
      lastResultRef.current = props.result;
      for (const cb of endHandlersRef.current) cb(props.result);
      setPhase('ended');
    }
  }, [props.result]);

  useEffect(() => {
    let cancelled = false;
    let app: import('pixi.js').Application | null = null;
    let game: ClientGame | null = null;
    let host: HTMLDivElement | null = null;

    // Tag the body while a game is mounted so global CSS can disable
    // user-select / iOS touch-callout on every element. Without this,
    // press-and-hold a sprint/shoot button selects on-screen text and
    // turns the screen blue.
    document.body.classList.add('in-game');

    void (async () => {
      try {
        const [gameModule, manifest] = await Promise.all([
          loadGame(props.gameId),
          loadGameManifest(props.gameId),
        ]);
        // ── webgl3d path (Total-Redesign-Spec §1a, wave 1) ─────────────
        // Flagship 3D games bring their own Three.js canvas. The host
        // creates NO Pixi Application (so pixi.js never even loads for a
        // pure-3D session) and ctx.pixi becomes a descriptive-throw stub.
        const is3D = manifest?.displayMode === 'webgl3d';
        let pixiMod: typeof import('pixi.js') | null = null;
        if (!is3D) {
          await import('pixi.js/unsafe-eval');
          pixiMod = await import('pixi.js');
          // Default Text.resolution → 4 so text stays crisp under fitStageToCanvas's stage.scale.
          const TextProto = (pixiMod.Text as unknown as { prototype: object }).prototype;
          const proto = Object.getPrototypeOf(TextProto) as object;
          type FlagBag = { __ttResPatched?: boolean };
          if (!((proto as FlagBag).__ttResPatched)) {
            const desc = Object.getOwnPropertyDescriptor(proto, 'resolution');
            if (desc?.set && desc?.get) {
              const origSet = desc.set;
              Object.defineProperty(proto, 'resolution', {
                ...desc,
                set(value: number | null) {
                  origSet.call(this, value == null ? 4 : value);
                },
              });
            }
            (proto as FlagBag).__ttResPatched = true;
          }
        }
        if (cancelled || !containerRef.current) return;
        if (!gameModule) {
          setBootError(`Unknown game: ${props.gameId}`);
          return;
        }

        const root = document.createElement('div');
        root.className = 'game-host';
        containerRef.current.appendChild(root);
        host = root;

        // Touch-device controls slot — reserve a strip BELOW the canvas
        // inside `.game-host` for games' action buttons / inventory
        // pickers / shape pools (audit F0.3, 2026-05-16). Games append
        // DOM here with `position: static` so the canvas isn't occluded
        // by overlays. Slot is hidden on desktop (mouse + keyboard
        // covers the input surface already; keeping the canvas tall is
        // more important there). The canvas resizes dynamically via a
        // ResizeObserver below — games that don't use the slot get a
        // full-height canvas; games that do append controls trigger a
        // shrink to match the slot's height.
        const isTouchHost = ('ontouchstart' in window) || ((navigator.maxTouchPoints ?? 0) > 0);
        const controlsSlot = document.createElement('div');
        controlsSlot.className = 'game-host-controls';
        if (!isTouchHost) controlsSlot.style.display = 'none';

        const rect = containerRef.current.getBoundingClientRect();
        const w = Math.max(280, Math.floor(rect.width || window.innerWidth - 32));
        // Fullscreen pass (2026-05-16): use the actual container height
        // — no longer cap the fallback at 60% of innerHeight. The
        // `.game-stage` container is now flex:1 inside `.play-screen`
        // (100dvh) so its rect.height is roughly the viewport height
        // minus the slim header + footer + safe-area-insets.
        const hRaw = Math.max(360, Math.floor(rect.height || (window.innerHeight - 120)));
        let h = hRaw;

        // Mobile thermal budget: cap render to 30 Hz on touch devices
        // (was implicit 60 Hz). Halves GPU + CPU per-frame work without
        // dropping resolution — the crisp text + shapes stay; only the
        // animation cadence is slower (imperceptible for these games).
        const isMobile = (window.innerWidth < 768) || ('ontouchstart' in window);
        if (!is3D && pixiMod) {
        const { Application } = pixiMod;
        app = new Application();
        await app.init({
          width: w, height: h,
          background: '#0d0a14',
          antialias: true,
          autoDensity: true,
          // Use full device pixel ratio (uncapped) for sharp text and
          // shapes after the fullscreen pass (2026-05-16). The earlier
          // cap at 2× was a memory hedge from when the canvas was a
          // small 60vh frame; with the canvas now filling the viewport,
          // a DPR-3 phone capped at 2× upscales each text bitmap 1.5×
          // — visible pixelation on serif fonts (user-reported on
          // rope-walk "ROPE 2 — 8 FLOORS" banner). Removing the cap
          // makes the framebuffer match device pixels 1:1.
          resolution: window.devicePixelRatio ?? 1,
        });
        if (cancelled) {
          app.destroy(true, { children: true, texture: true });
          return;
        }
        if (isMobile) app.ticker.maxFPS = 30;
        const canvas = app.canvas;
        canvas.style.display = 'block';
        // Fill the available host area in BOTH dimensions (2026-05-16
        // fullscreen pass). The onResize callback below keeps the
        // renderer's intrinsic w/h in sync with the host's rect, so
        // there's no distortion — DOM size and renderer size move
        // together. Earlier `width: 100%; height: auto` meant the
        // canvas was width-driven and left empty bands above/below
        // whenever the container aspect didn't match boot aspect.
        canvas.style.width = '100%';
        canvas.style.height = '100%';
        canvas.style.touchAction = 'none';
        canvas.style.borderRadius = '12px';
        root.appendChild(canvas);
        } // end !is3D — webgl3d games append their own canvas in start()
        // Pause the Pixi ticker when the tab is backgrounded. Browsers
        // throttle rAF to ~1Hz on hidden tabs, but the ticker still
        // wakes up and drives Pixi each tick — wasted work + battery.
        // (No-op for webgl3d: app stays null; @tt/shell-3d pauses its
        // own rAF loop on visibilitychange.)
        const onVisibility = () => {
          if (!app) return;
          if (document.hidden) app.ticker.stop();
          else app.ticker.start();
        };
        document.addEventListener('visibilitychange', onVisibility);
        // Controls slot appended AFTER canvas — flex-column order in
        // `.game-host` puts it below the canvas (see global.css).
        root.appendChild(controlsSlot);

        // ── Shell controls layer (Total-Redesign-Spec §1c, wave 0) ────
        // Mounted only when the game's manifest declares a declarative
        // `controlScheme`. Legacy games (no controlScheme) keep the
        // controlsSlot + per-game input paths above, fully untouched.
        let controlsLayer: ControlsLayerHandle | null = null;
        if (manifest?.controlScheme) {
          controlsLayer = createControlsLayer(root, manifest.controlScheme, {
            platform: detectPlatform(),
            gameId: props.gameId,
          });
        }

        // ── StageViewport plumbing (Total-Redesign-Spec §1b, wave 0) ──
        // GameStage's resize path is the single source of truth: after
        // every renderer.resize() we build a StageViewport and
        // synchronously notify ctx.onViewport subscribers + game.layout().
        const viewportSubs = new Set<(v: StageViewport) => void>();
        // One-time probe div to read CSS env(safe-area-inset-*) values.
        const safeProbe = document.createElement('div');
        safeProbe.style.cssText =
          'position:fixed;left:0;top:0;width:0;height:0;visibility:hidden;pointer-events:none;' +
          'padding-top:env(safe-area-inset-top,0px);padding-right:env(safe-area-inset-right,0px);' +
          'padding-bottom:env(safe-area-inset-bottom,0px);padding-left:env(safe-area-inset-left,0px);';
        document.body.appendChild(safeProbe);
        const readSafeArea = () => {
          const cs = getComputedStyle(safeProbe);
          return {
            top: parseFloat(cs.paddingTop) || 0,
            right: parseFloat(cs.paddingRight) || 0,
            bottom: parseFloat(cs.paddingBottom) || 0,
            left: parseFloat(cs.paddingLeft) || 0,
          };
        };
        const buildViewport = (): StageViewport => {
          // webgl3d: no Pixi renderer — the host rect IS the stage size
          // (the game's own canvas fills the host via CSS).
          const hostRect = is3D ? containerRef.current?.getBoundingClientRect() : undefined;
          const vw = app?.screen.width ?? (hostRect?.width ? Math.floor(hostRect.width) : w);
          const vh = app?.screen.height ?? (hostRect?.height ? Math.floor(hostRect.height) : h);
          return {
            width: vw,
            height: vh,
            dpr: window.devicePixelRatio ?? 1,
            orientation: vw >= vh ? 'landscape' : 'portrait',
            safeArea: readSafeArea(),
            controlsRect: controlsLayer?.getControlsRect() ?? null,
            hudRects: [], // shell HUD lands in a later wave
          };
        };
        let currentViewport = buildViewport();
        const notifyViewport = () => {
          const v = buildViewport();
          currentViewport = v;
          for (const cb of viewportSubs) {
            try { cb(v); } catch (e) { console.error('[GameStage] onViewport subscriber', e); }
          }
          try { game?.layout?.(v); } catch (e) { console.error('[GameStage] game.layout', e); }
        };

        const onResize = () => {
          if (!containerRef.current) return;
          const r = containerRef.current.getBoundingClientRect();
          const nw = Math.max(280, Math.floor(r.width || w));
          // Subtract the controls slot's current visible height so the
          // canvas doesn't stretch into the touch-controls strip. Both
          // width AND height refresh — earlier only width updated, so
          // phone rotation kept the canvas at the portrait aspect
          // (audit fullscreen pass 2026-05-16).
          const slotH = controlsSlot.offsetHeight || 0;
          const targetH = Math.max(280, Math.floor((r.height || hRaw)) - slotH);
          h = targetH;
          if (app) {
            app.renderer.resize(nw, targetH);
            if (app.stage) app.stage.hitArea = app.screen;
          }
          // webgl3d: the game's own canvas resizes via CSS + its shell's
          // ResizeObserver; we still rebuild + broadcast the viewport.
          notifyViewport();
        };
        window.addEventListener('resize', onResize);
        window.addEventListener('orientationchange', onResize);

        // ResizeObserver on the controls slot — when a game appends
        // touch controls to it, the slot's offsetHeight jumps (via the
        // `.game-host-controls:empty` collapse rule in global.css) and
        // we shrink the canvas to match (audit F0.3). When controls
        // are removed, slot collapses and canvas grows back. Cleaned
        // up in the host __cleanup hook. Reads the container's CURRENT
        // rect.height (not boot-time hRaw) so landscape rotation also
        // re-fits correctly (audit landscape, 2026-05-16).
        let slotObs: ResizeObserver | null = null;
        if (typeof ResizeObserver !== 'undefined') {
          slotObs = new ResizeObserver(() => {
            if (!containerRef.current) return;
            const slotH = controlsSlot.offsetHeight || 0;
            const ch = containerRef.current.getBoundingClientRect().height || hRaw;
            const targetH = Math.max(280, Math.floor(ch - slotH));
            if (app) {
              if (Math.abs(app.renderer.height - targetH) > 4) {
                h = targetH;
                app.renderer.resize(app.renderer.width, targetH);
                if (app.stage) app.stage.hitArea = app.screen;
                notifyViewport();
              }
            } else if (Math.abs(h - targetH) > 4) {
              // webgl3d: no renderer to resize; just rebroadcast.
              h = targetH;
              notifyViewport();
            }
          });
          slotObs.observe(controlsSlot);
          // Also observe the container so window-resize → host-resize
          // chain refits the canvas without waiting for the slot to
          // change shape.
          slotObs.observe(containerRef.current);
        }

        setPhase('countdown');
        const countdownInterval = window.setInterval(() => {
          if (Date.now() >= props.startsAt) {
            setPhase('playing');
            window.clearInterval(countdownInterval);
          }
        }, 80);

        // Audio cues for 3-2-1-GO are fired by each per-game client during
        // its own warmup phase (themed to the game). Firing them ALSO from
        // the host produced doubled beats. Keep these no-ops to preserve
        // the cleanup contract (`clearInterval(beatInterval)`).
        const beatInterval = window.setInterval(() => { /* host countdown audio disabled */ }, 80);
        const goTimer = window.setTimeout(() => {
          window.clearInterval(beatInterval);
        }, Math.max(0, props.startsAt - Date.now()));

        // webgl3d games receive a throwing stub instead of an Application:
        // the field is type-required on ClientGameContext, but no Pixi app
        // exists. Any accidental access fails loudly with a pointed message
        // rather than a silent undefined-deref deep inside Pixi.
        const pixiForCtx: import('pixi.js').Application = app ?? (new Proxy({}, {
          get(_t, prop) {
            throw new Error(
              `ctx.pixi.${String(prop)} accessed, but '${props.gameId}' is displayMode:webgl3d — ` +
              'no Pixi Application exists. 3D games must render into their own canvas in ctx.root.',
            );
          },
        }) as unknown as import('pixi.js').Application);

        const ctx: ClientGameContext = {
          root,
          // Provide the touch-controls slot so games can mount action
          // buttons / inventory pickers BELOW the canvas (audit F0.3).
          // Slot is undefined on non-touch hosts so games fall back to
          // their existing in-canvas positioning.
          ...(isTouchHost ? { controlsSlot } : {}),
          // Shell controls layer handle — present only when the manifest
          // declares a controlScheme (§1c).
          ...(controlsLayer ? { controls: controlsLayer.controls } : {}),
          // Viewport snapshot (§1b) — getter so ctx.viewport always
          // reflects the latest resize, while onViewport still fires
          // synchronously after every renderer.resize().
          get viewport() { return currentViewport; },
          onViewport: (cb) => {
            viewportSubs.add(cb);
            return () => { viewportSubs.delete(cb); };
          },
          pixi: pixiForCtx,
          mySlot: props.mySlot,
          players: props.players,
          startsAt: props.startsAt,
          endsAt: props.endsAt,
          feedback: {
            tick: () => audio.sfx.tap(),
            success: () => audio.sfx.success(),
            fail: () => audio.sfx.fail(),
            cheer: () => audio.sfx.cheer(),
            bell: () => audio.sfx.bell(),
          },
          net: {
            sendInput: (payload) => props.sendInput(payload),
            onTick: (cb) => {
              tickHandlersRef.current.add(cb);
              if (lastTickRef.current !== undefined) cb(lastTickRef.current);
              return () => { tickHandlersRef.current.delete(cb); };
            },
            onEnd: (cb) => { endHandlersRef.current.add(cb); return () => { endHandlersRef.current.delete(cb); }; },
            onStart: (cb) => { startHandlersRef.current.add(cb); cb(props.params); return () => { startHandlersRef.current.delete(cb); }; },
          },
        };

        game = gameModule;
        audio.setAmbience(GAME_AMBIENCE[props.gameId] ?? 'tavern');
        // Pre-rasterize each player's full lobby SVG icon into a Pixi
        // Texture so drawCharacter / CharacterSprite renders the actual
        // chosen character pixel-for-pixel instead of the simpler Pixi
        // primitive fallback. For webgl3d there's no Pixi app, so instead
        // register raw face CANVASES on the global registry that shell-3d's
        // faceTextureForIcon reads (wraps them in a THREE.CanvasTexture for
        // the figure's head). Both are best-effort: a rasterize failure
        // must not block the game boot.
        if (app) {
          await preloadMatchCharacterTextures(props.players, app);
        } else {
          await preloadMatch3DFaceCanvases(props.players).catch((e) =>
            console.warn('[GameStage] 3D face preload failed', e),
          );
        }
        await game.start(ctx);

        (host as HTMLDivElement & { __cleanup?: () => void }).__cleanup = () => {
          window.removeEventListener('resize', onResize);
          window.removeEventListener('orientationchange', onResize);
          document.removeEventListener('visibilitychange', onVisibility);
          window.clearInterval(countdownInterval);
          window.clearInterval(beatInterval);
          window.clearTimeout(goTimer);
          try { slotObs?.disconnect(); } catch { /* */ }
          try { controlsLayer?.destroy(); } catch { /* */ }
          viewportSubs.clear();
          safeProbe.remove();
        };
      } catch (e) {
        console.error('[GameStage] boot failed', e);
        setBootError((e as Error).message ?? 'unknown error');
      }
    })();

    return () => {
      cancelled = true;
      document.body.classList.remove('in-game');
      // Restore lobby mood + tavern ambience when leaving the game so the
      // per-game music doesn't keep playing on the room screen / between
      // rounds in Free mode.
      audio.setMood('lobby');
      audio.setAmbience('tavern');
      clearMatchCharacterTextures();
      void (async () => {
        try { if (game) await game.stop(); } catch (e) { console.error('game stop', e); }
        try {
          if (host && (host as HTMLDivElement & { __cleanup?: () => void }).__cleanup) {
            (host as HTMLDivElement & { __cleanup?: () => void }).__cleanup!();
          }
        } catch { /* */ }
        try { if (host && host.parentElement) host.parentElement.removeChild(host); } catch { /* */ }
        try { if (app) app.destroy(true, { children: true, texture: true }); } catch { /* */ }
        tickHandlersRef.current.clear();
        endHandlersRef.current.clear();
        startHandlersRef.current.clear();
        lastTickRef.current = undefined;
        lastResultRef.current = null;
      })();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.matchId, props.gameId]);

  // Games that render their OWN full-canvas environmental backdrop
  // (so the shared tavern-wall sconces/stone would visually clash
  // with the game's atmosphere). Lumber Tumble draws a forest-gorge
  // scene inside the canvas and out into the canvas overscan, so the
  // tavern sconces leaking through the gutter looked like a stone-keep
  // wall framing a forest — confusing visual mismatch.
  const ENV_BACKDROP_GAMES = new Set(['lumber-tumble']);
  const showSharedBackdrop = !ENV_BACKDROP_GAMES.has(props.gameId);

  return (
    <>
      {/* Shared tavern-wall backdrop that fills the empty side gutters
       * around the centered canvas on wide PC viewports. Fixed-positioned
       * at z-index 0 behind #root (z-index 1, isolation: isolate), pure
       * CSS + inline SVG, hidden under 1100px. Single shared design across
       * every game — the game canvas always stays the visual focus.
       * Skipped for games with their own environmental backdrop
       * (see ENV_BACKDROP_GAMES above) to avoid visual conflict. */}
      {showSharedBackdrop && <StageBackdrop />}
      <div ref={containerRef} className="game-stage">
        {bootError && (
        <div className="game-boot-error" role="alert">
          <strong>Couldn't load game.</strong>
          <span className="muted">{bootError}</span>
          <span className="muted">This can happen after an update or interrupted mobile load.</span>
          <button type="button" className="btn primary" onClick={() => window.location.reload()}>Retry</button>
        </div>
      )}
      {/* The React-side CountdownOverlay was causing a "double countdown"
          UX — every per-game client also draws its own 3-2-1-GO during
          the server warmup phase, themed to the game (sky-sieve has stars,
          cellar-sweep has dim torches, etc.). Render the per-game countdown
          only — the host now passes silently from boot → playing.
          InputHintBadge removed 2026-05-12 — covered the canvas during play;
          UniversalMatchIntro shows the same key+touch hint pre-round. */}
      </div>
    </>
  );
}
